import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Heart, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useCart } from "../context/CartContext";
import { useStore } from "../context/StoreContext";
import { Gallery } from "../components/store/Gallery";
import { SizeChart } from "../components/store/SizeChart";
import { Price } from "../components/store/Price";
import { QtyStepper } from "../components/store/CartSheet";
import { ProductRail } from "../components/store/ProductRail";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Skeleton } from "../components/ui/skeleton";

const Swatches = ({ p, size, color, setSize, setColor, stockOf }) => {
  const { t, L } = useLang();
  return (
    <>
      {p.colors?.length > 0 && (
        <div className="mt-8">
          <p className="mb-3 text-xs tracking-[0.1em] text-sand-dark">{t("color")}: <span className="text-ink">{color && L(p.colors.find((c) => c.name_ar === color), "name")}</span></p>
          <div className="flex gap-3">
            {p.colors.map((c) => (
              <button key={c.name_ar} data-testid={`color-option-${c.name_en || c.name_ar}`} onClick={() => setColor(c.name_ar)} title={L(c, "name")}
                className={`h-9 w-9 rounded-full border p-1 transition-colors ${color === c.name_ar ? "border-ink" : "border-sand"}`}>
                <span className="block h-full w-full rounded-full" style={{ background: c.hex }} />
              </button>
            ))}
          </div>
        </div>
      )}
      {p.sizes?.length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs tracking-[0.1em] text-sand-dark">{t("size")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {p.sizes.map((s) => {
              const out = stockOf(s, color) <= 0;
              return (
                <button key={s} disabled={out} data-testid={`size-option-${s}`} onClick={() => setSize(s)}
                  className={`h-11 min-w-[52px] border px-3 text-sm transition-colors ${size === s ? "border-ink bg-ink text-ivory" : "border-sand hover:border-ink"} disabled:cursor-not-allowed disabled:text-sand disabled:line-through`}>
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default function ProductPage() {
  const { slug } = useParams();
  const { t, L } = useLang();
  const { settings, zones } = useStore();
  const { add, setOpen, wishlist, toggleWish } = useCart();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setData(null);
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data);
      const p = r.data.product;
      setColor(p.colors?.[0]?.name_ar || "");
      setSize("");
      setQty(1);
    }).catch(() => setData(false));
  }, [slug]);

  const p = data?.product;
  const stockOf = useMemo(() => (s, c) => p?.variants?.find((v) => (v.size || "") === (s || "") && (v.color || "") === (c || ""))?.stock ?? 0, [p]);

  if (data === false) return <p className="container-q py-32 text-center font-display text-2xl">{t("no_results")}</p>;
  if (!p) return <div className="container-q grid gap-10 py-14 lg:grid-cols-2"><Skeleton className="aspect-[3/4] rounded-none bg-sand-light" /><Skeleton className="h-80 rounded-none bg-sand-light" /></div>;

  const needSize = p.sizes?.length > 0;
  const chosen = (!needSize || size) && (!p.colors?.length || color);
  const stock = chosen ? stockOf(size, color) : null;
  const unavailable = p.status === "unavailable" || p.total_stock === 0;
  const dMin = zones.length ? Math.min(...zones.map((z) => z.days_min)) : 1;
  const dMax = zones.length ? Math.max(...zones.map((z) => z.days_max)) : 5;
  const canBuy = chosen && stock > 0 && !unavailable;

  const addToCart = (go) => {
    if (!chosen) return toast.error(t("select_variant"));
    if (!canBuy) return toast.error(t("out_of_stock"));
    add({ product_id: p.id, slug: p.slug, name_ar: p.name_ar, name_en: p.name_en, image: p.images?.[0], price: p.final_price, size, color, qty: Math.min(qty, stock), max: stock });
    if (go) nav("/checkout");
    else { toast.success(t("added_cart")); setOpen(true); }
  };

  const liked = wishlist.includes(p.id);

  return (
    <div data-testid="product-page">
      <div className="container-q grid gap-10 py-10 sm:py-14 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-7"><Gallery images={p.images} alt={L(p, "name")} /></div>
        <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
          <p className="eyebrow mb-4">{p.sku}</p>
          <h1 data-testid="product-title" className="text-4xl leading-tight sm:text-5xl">{L(p, "name")}</h1>
          <Price price={p.price} final={p.final_price} className="mt-5 text-xl" testid="product-price" />
          <p className="mt-2 text-xs text-sand-dark">{t("vat_included")}</p>

          <Swatches p={p} size={size} color={color} setSize={setSize} setColor={setColor} stockOf={stockOf} />
          {p.show_size_chart && needSize && <div className="mt-4"><SizeChart rows={settings?.size_chart} /></div>}

          <p data-testid="stock-status" className={`mt-6 text-sm ${unavailable || stock === 0 ? "text-destructive" : "text-ink/70"}`}>
            {unavailable ? t("out_of_stock") : !chosen ? t("select_variant") : stock === 0 ? t("out_of_stock") : stock <= 3 ? t("only_left", { n: stock }) : `${t("in_stock")} · ${stock} ${t("pieces")}`}
          </p>

          <div className="mt-6 flex items-center gap-3">
            <QtyStepper value={qty} max={stock || 1} onChange={setQty} testid="product-qty" />
            <button data-testid="product-wishlist-button" onClick={() => toast(toggleWish(p.id) ? t("added_wish") : t("removed_wish"))}
              className="grid h-9 w-9 place-items-center border border-sand transition-colors hover:border-ink" aria-label="wishlist">
              <Heart size={16} strokeWidth={1.4} className={liked ? "fill-ink" : ""} />
            </button>
          </div>
          <div className="mt-6 grid gap-3">
            <button data-testid="add-to-cart-button" disabled={unavailable} onClick={() => addToCart(false)} className="btn-ink w-full">{t("add_to_cart")}</button>
            <button data-testid="buy-now-button" disabled={unavailable} onClick={() => addToCart(true)} className="btn-line w-full disabled:opacity-40">{t("buy_now")}</button>
          </div>

          <div className="mt-8 flex items-start gap-3 bg-sand-light p-5 text-sm">
            <Truck size={18} strokeWidth={1.3} className="mt-0.5 shrink-0 text-gold-deep" />
            <p data-testid="shipping-estimate" className="leading-6">
              {t("delivery_est", { a: dMin, b: dMax })}. {p.shipping_rule === "free" ? t("free") + " — " + t("shipping") : ""}
            </p>
          </div>

          <Accordion type="single" collapsible defaultValue="desc" className="mt-8">
            {[["desc", "description", "description"], ["fabric", "fabric", "fabric"], ["care", "care", "care"]].map(([v, k, f]) => L(p, f) && (
              <AccordionItem key={v} value={v} className="border-sand">
                <AccordionTrigger data-testid={`accordion-${v}`} className="text-sm tracking-[0.08em] hover:no-underline">{t(k)}</AccordionTrigger>
                <AccordionContent className="text-sm leading-7 text-ink/75">{L(p, f)}</AccordionContent>
              </AccordionItem>
            ))}
            <AccordionItem value="ship" className="border-sand">
              <AccordionTrigger className="text-sm tracking-[0.08em] hover:no-underline">{t("shipping_info")}</AccordionTrigger>
              <AccordionContent className="text-sm leading-7 text-ink/75">{L(settings?.pages, "shipping")}</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
      <ProductRail testid="related-products" eyebrow="More" title={t("related")} products={data.related} />
    </div>
  );
}
